import type { ReactElement, ReactNode } from "react";
import { XIcon } from "@phosphor-icons/react";
import {
  Dialog as AriaDialog,
  DialogTrigger,
  Heading,
  Menu,
  MenuItem,
  MenuSection,
  MenuTrigger,
  Modal,
  ModalOverlay,
  Popover,
  Separator,
  Tooltip as AriaTooltip,
  TooltipTrigger,
  Header,
  type Key,
} from "react-aria-components";
import { Button, type ButtonVariant } from "./button.tsx";
import { cx } from "../cx.ts";

interface DialogShellProps {
  title: ReactNode;
  description?: ReactNode;
  children?: ReactNode;
  footer?: ReactNode | ((close: () => void) => ReactNode);
  closeLabel: string;
  isOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  wide?: boolean;
  /** Alleen sluiten via de knoppen (bv. bij een formulier met wijzigingen). */
  isDismissable?: boolean;
  role?: "dialog" | "alertdialog";
}

function DialogContent({ title, description, children, footer, closeLabel, role }: DialogShellProps) {
  return (
    <AriaDialog className="ui-dialog" role={role}>
      {({ close }) => (
        <>
          <div className="ui-dialog__head">
            <div className="stack" style={{ ["--gap" as string]: "var(--space-1)" }}>
              <Heading slot="title">{title}</Heading>
              {description ? <p className="muted">{description}</p> : null}
            </div>
            <Button variant="ghost" size="sm" iconOnly aria-label={closeLabel} icon={<XIcon aria-hidden />} onPress={close} />
          </div>
          {children ? <div className="ui-dialog__body">{children}</div> : null}
          {footer ? <div className="ui-dialog__foot">{typeof footer === "function" ? footer(close) : footer}</div> : null}
        </>
      )}
    </AriaDialog>
  );
}

/** Gecontroleerde modale dialoog. */
export function Dialog(props: DialogShellProps) {
  return (
    <ModalOverlay className="ui-modal-overlay" isOpen={props.isOpen} onOpenChange={props.onOpenChange} isDismissable={props.isDismissable ?? true}>
      <Modal className={cx("ui-modal", props.wide && "ui-modal--wide")}>
        <DialogContent {...props} />
      </Modal>
    </ModalOverlay>
  );
}

/** Zijpaneel voor details en bewerken zonder de lijst te verlaten. */
export function Sheet(props: DialogShellProps & { width?: string }) {
  return (
    <ModalOverlay className="ui-sheet-overlay" isOpen={props.isOpen} onOpenChange={props.onOpenChange} isDismissable={props.isDismissable ?? true}>
      <Modal className="ui-sheet" style={props.width ? { ["--sheet-w" as string]: props.width } : undefined}>
        <DialogContent {...props} />
      </Modal>
    </ModalOverlay>
  );
}

export { DialogTrigger };

export interface ConfirmProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  title: ReactNode;
  body?: ReactNode;
  confirmLabel: string;
  cancelLabel: string;
  closeLabel: string;
  tone?: "primary" | "danger";
  isPending?: boolean;
  /** Bevestigen kan pas als de inhoud compleet is (bijvoorbeeld een verplichte controlelijst). */
  isConfirmDisabled?: boolean;
  onConfirm: () => void;
  children?: ReactNode;
}

/** Bevestiging voor onomkeerbare of zware acties. De bevestigknop zegt precies wat er gebeurt. */
export function ConfirmDialog({ isOpen, onOpenChange, title, body, confirmLabel, cancelLabel, closeLabel, tone = "primary", isPending, isConfirmDisabled, onConfirm, children }: ConfirmProps) {
  return (
    <Dialog
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      title={title}
      description={body}
      closeLabel={closeLabel}
      role="alertdialog"
      footer={(close) => (
        <>
          <Button variant="ghost" onPress={close}>
            {cancelLabel}
          </Button>
          <Button variant={tone as ButtonVariant} isPending={isPending} isDisabled={isConfirmDisabled} onPress={onConfirm}>
            {confirmLabel}
          </Button>
        </>
      )}
    >
      {children}
    </Dialog>
  );
}

export interface MenuAction {
  id: Key;
  label: string;
  icon?: ReactNode;
  danger?: boolean;
  isDisabled?: boolean;
  shortcut?: string;
  section?: string;
}

/** Knop met actiemenu. Acties met hetzelfde `section` worden gegroepeerd. */
export function ActionMenu({ trigger, actions, onAction, label }: { trigger: ReactElement; actions: MenuAction[]; onAction: (id: Key) => void; label: string }) {
  const sections = [...new Set(actions.map((a) => a.section ?? ""))];
  return (
    <MenuTrigger>
      {trigger}
      <Popover className="ui-popover" placement="bottom end">
        <Menu className="ui-menu" aria-label={label} onAction={onAction}>
          {sections.map((section, i) => (
            <MenuSection key={section || `s${i}`}>
              {i > 0 ? <Separator className="ui-separator" /> : null}
              {section ? <Header className="ui-menu-section-head">{section}</Header> : null}
              {actions
                .filter((a) => (a.section ?? "") === section)
                .map((a) => (
                  <MenuItem key={String(a.id)} id={a.id} isDisabled={a.isDisabled} textValue={a.label} className={cx("ui-menu-item", a.danger && "ui-menu-item--danger")}>
                    {a.icon}
                    <span>{a.label}</span>
                    {a.shortcut ? <kbd className="ui-menu-item__kbd">{a.shortcut}</kbd> : null}
                  </MenuItem>
                ))}
            </MenuSection>
          ))}
        </Menu>
      </Popover>
    </MenuTrigger>
  );
}

export function Tooltip({ content, children, delay = 500 }: { content: ReactNode; children: ReactElement; delay?: number }) {
  return (
    <TooltipTrigger delay={delay}>
      {children}
      <AriaTooltip className="ui-tooltip" offset={6}>
        {content}
      </AriaTooltip>
    </TooltipTrigger>
  );
}
